import { useId, type ReactNode } from 'react';
import { Icon, IdentityChip } from '../ui';

/**
 * One row of the Build and My apps lists, laid out like the phone's (src/app/(main)/(tabs)/index.tsx):
 * a full-width card with the app's mark, its name on one line, where it lives and when it last
 * changed, and an Open pill INSIDE the card on the right — or a chevron when there is no page.
 *
 * The web rows put Open beside the card, which squeezed every card into a column with a loose link
 * next to it. The card is now a container rather than a button: the main area is the button that
 * opens the editor, and the pill is a sibling link in the same card, so the HTML stays valid (no
 * link inside a button) and the whole card still reads as one thing.
 */
export default function ProjectCard({ name, onOpenEditor, href, address, meta, note, label }: {
  name: string;
  /** Opens the editor. Absent for an app with no project in this browser: the card is then the link. */
  onOpenEditor?: () => void;
  /** The live page, when there is one. */
  href?: string | null;
  /** Where the page lives, as people read it. */
  address?: string | null;
  /** When it last changed, or a status such as "Not built yet". */
  meta?: ReactNode;
  /** A second caption line, e.g. "Made on another device". */
  note?: ReactNode;
  /** The main area's accessible name, when its text alone would not say enough. */
  label?: string;
}) {
  const noteId = useId();
  const describedBy = note ? noteId : undefined;
  const body = (
    <>
      <IdentityChip name={name} size={46} />
      <span className="project-text">
        <span className="type-heading project-name">{name}</span>
        {address && <span className="type-caption project-address"><bdi>{address}</bdi></span>}
        {meta && <span className="type-caption project-meta">{meta}</span>}
        {note && <span id={noteId} className="type-caption project-meta">{note}</span>}
      </span>
    </>
  );

  return (
    <li className="project-item">
      <div className="card clay-card project-card">
        {onOpenEditor ? (
          <button type="button" className="project-card-main" onClick={onOpenEditor} aria-label={label} aria-describedby={describedBy}>
            {body}
          </button>
        ) : (
          <a className="project-card-main" href={href ?? undefined} target="_blank" rel="noreferrer" aria-label={label} aria-describedby={describedBy}>
            {body}
          </a>
        )}
        {onOpenEditor && href ? (
          <a className="project-open-pill" href={href} target="_blank" rel="noreferrer" aria-label={`Open ${name} in a new tab`}>
            Open <span aria-hidden="true">↗</span>
          </a>
        ) : onOpenEditor ? (
          <span className="project-chevron" aria-hidden="true"><Icon name="chevron" size={18} /></span>
        ) : (
          // The whole card is the link to the page; this says it leaves for a new tab.
          <span className="project-chevron" aria-hidden="true">↗</span>
        )}
      </div>
    </li>
  );
}
